import { useEffect, useState } from "react";
import AdminLayout, { PageHeader } from "../../components/admin/AdminLayout";
import api, { formatApiError } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Switch } from "../../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { Plus, PencilSimple, Trash, Question } from "@phosphor-icons/react";
import { SortableProvider, SortableItems, SortableItem, DragHandle } from "../../components/admin/SortableList";

const SCOPES = [
  { value: "global", label: "Global / Site-wide" },
  { value: "product", label: "Specific product" },
  { value: "blog", label: "Specific blog post" },
  { value: "page", label: "Page key (about, contact, …)" },
];

const empty = { question: "", answer: "", scope: "global", scope_id: "", category: "", is_active: true };

export default function Faqs() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [blogs, setBlogs] = useState([]);
  const [scopeFilter, setScopeFilter] = useState("global");
  const [scopeIdFilter, setScopeIdFilter] = useState("");
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);

  const load = async () => {
    const params = { scope: scopeFilter };
    if (scopeIdFilter) params.scope_id = scopeIdFilter;
    const { data } = await api.get("/admin/faqs", { params });
    setItems(data);
  };

  useEffect(() => {
    api.get("/admin/products").then((r) => setProducts(r.data));
    api.get("/admin/blogs").then((r) => setBlogs(r.data));
  }, []);
  useEffect(() => { load(); }, [scopeFilter, scopeIdFilter]);

  const openNew = () => {
    setEditing(null);
    setForm({ ...empty, scope: scopeFilter, scope_id: scopeFilter !== "global" ? scopeIdFilter : "" });
    setOpen(true);
  };
  const openEdit = (it) => {
    setEditing(it);
    setForm({ ...empty, ...it, scope_id: it.scope_id || "" });
    setOpen(true);
  };

  const save = async () => {
    try {
      const payload = {
        ...form,
        scope_id: form.scope === "global" ? null : form.scope_id || null,
      };
      if (editing) await api.put(`/admin/faqs/${editing.id}`, payload);
      else await api.post("/admin/faqs", payload);
      toast.success("Saved"); setOpen(false); load();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  const remove = async (id) => {
    if (!confirm("Delete FAQ?")) return;
    try { await api.delete(`/admin/faqs/${id}`); load(); }
    catch (e) { toast.error(formatApiError(e)); }
  };

  const scopeIdOptions = () => {
    if (form.scope === "product") return products.map((p) => ({ value: p.id, label: p.name }));
    if (form.scope === "blog") return blogs.map((b) => ({ value: b.id, label: b.title }));
    return [];
  };

  const scopeIdFilterOptions = () => {
    if (scopeFilter === "product") return products.map((p) => ({ value: p.id, label: p.name }));
    if (scopeFilter === "blog") return blogs.map((b) => ({ value: b.id, label: b.title }));
    return [];
  };

  return (
    <AdminLayout>
      <PageHeader
        title="FAQs"
        subtitle="Frequently asked questions — site-wide, per product, per blog or per page. Auto-published as FAQPage rich snippets."
        actions={
          <Button onClick={openNew} data-testid="add-faq-btn" className="bg-[#C2410C] hover:bg-[#9A3412] text-white">
            <Plus size={16} className="mr-2" />New FAQ
          </Button>
        }
      />

      <div className="p-8 space-y-4">
        <div className="bg-white border border-[#E2E8F0] rounded-lg p-4 flex flex-wrap gap-3 items-end">
          <div className="space-y-1">
            <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4B5563]">Scope</Label>
            <div className="w-56">
              <Select value={scopeFilter} onValueChange={(v) => { setScopeFilter(v); setScopeIdFilter(""); }}>
                <SelectTrigger data-testid="faq-scope-filter"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
          </div>
          {(scopeFilter === "product" || scopeFilter === "blog") && (
            <div className="space-y-1">
              <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4B5563]">{scopeFilter === "product" ? "Product" : "Blog post"}</Label>
              <div className="w-72">
                <Select value={scopeIdFilter || "all"} onValueChange={(v) => setScopeIdFilter(v === "all" ? "" : v)}>
                  <SelectTrigger data-testid="faq-scopeid-filter"><SelectValue placeholder="All" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All {scopeFilter}s</SelectItem>
                    {scopeIdFilterOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            </div>
          )}
          {scopeFilter === "page" && (
            <div className="space-y-1">
              <Label className="text-xs tracking-[0.2em] uppercase font-semibold text-[#4B5563]">Page key</Label>
              <Input value={scopeIdFilter} onChange={(e) => setScopeIdFilter(e.target.value)} placeholder="about, contact, etc." className="w-56" data-testid="faq-pagekey-filter" />
            </div>
          )}
        </div>

        <SortableProvider items={items} setItems={setItems} reorderEndpoint="/admin/reorder/faqs">
          <div className="bg-white border border-[#E2E8F0] rounded-lg overflow-hidden" data-testid="faqs-table">
            <table className="w-full text-sm">
              <thead className="bg-[#F7F8F6] text-left text-[10px] tracking-[0.2em] uppercase text-[#4B5563]">
                <tr>
                  <th className="px-3 py-3 w-10"></th>
                  <th className="px-6 py-3">Question</th>
                  <th className="px-6 py-3">Category</th>
                  <th className="px-6 py-3">Active</th>
                  <th className="px-6 py-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#E2E8F0]">
                {items.length === 0 && <tr><td colSpan={5} className="px-6 py-10 text-center text-[#4B5563]">No FAQs in this scope yet.</td></tr>}
                <SortableItems items={items}>
                  {items.map((it) => (
                    <SortableItem key={it.id} id={it.id} as="tr" data-testid={`faq-row-${it.id}`}>
                      {(handleProps) => (
                        <>
                          <td className="px-3 py-3"><DragHandle handleProps={handleProps} /></td>
                          <td className="px-6 py-3">
                            <div className="font-medium text-[#1F2937] flex items-start gap-2">
                              <Question size={16} weight="duotone" className="text-[#C2410C] mt-0.5 flex-shrink-0" />
                              <span className="line-clamp-2">{it.question}</span>
                            </div>
                            <div className="text-xs text-[#4B5563] mt-1 line-clamp-2 pl-6">{it.answer}</div>
                          </td>
                          <td className="px-6 py-3 text-[#4B5563]">{it.category || "—"}</td>
                          <td className="px-6 py-3">
                            <span className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded ${it.is_active ? "bg-[#C2410C]/10 text-[#C2410C]" : "bg-[#E2E8F0] text-[#4B5563]"}`}>
                              {it.is_active ? "Visible" : "Hidden"}
                            </span>
                          </td>
                          <td className="px-6 py-3 text-right">
                            <button onClick={() => openEdit(it)} data-testid={`edit-${it.id}`} className="text-[#4B5563] hover:text-[#C2410C] mr-3"><PencilSimple size={16} /></button>
                            <button onClick={() => remove(it.id)} data-testid={`delete-${it.id}`} className="text-[#4B5563] hover:text-[#EF4444]"><Trash size={16} /></button>
                          </td>
                        </>
                      )}
                    </SortableItem>
                  ))}
                </SortableItems>
              </tbody>
            </table>
          </div>
        </SortableProvider>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-xl max-h-[90vh] overflow-y-auto" data-testid="faq-dialog">
          <DialogHeader><DialogTitle>{editing ? "Edit FAQ" : "New FAQ"}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div className="space-y-2">
              <Label>Scope</Label>
              <Select value={form.scope} onValueChange={(v) => setForm({ ...form, scope: v, scope_id: "" })}>
                <SelectTrigger data-testid="faq-scope"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {SCOPES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            {(form.scope === "product" || form.scope === "blog") && (
              <div className="space-y-2">
                <Label>{form.scope === "product" ? "Product" : "Blog post"}</Label>
                <Select value={form.scope_id || ""} onValueChange={(v) => setForm({ ...form, scope_id: v })}>
                  <SelectTrigger data-testid="faq-scopeid"><SelectValue placeholder="Select…" /></SelectTrigger>
                  <SelectContent>
                    {scopeIdOptions().map((o) => <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
            )}
            {form.scope === "page" && (
              <div className="space-y-2">
                <Label>Page key</Label>
                <Input value={form.scope_id} onChange={(e) => setForm({ ...form, scope_id: e.target.value })} placeholder="about, contact, careers, …" data-testid="faq-pagekey" />
              </div>
            )}
            <div className="space-y-2">
              <Label>Question *</Label>
              <Input value={form.question} onChange={(e) => setForm({ ...form, question: e.target.value })} data-testid="faq-question" />
            </div>
            <div className="space-y-2">
              <Label>Answer *</Label>
              <Textarea rows={5} value={form.answer} onChange={(e) => setForm({ ...form, answer: e.target.value })} data-testid="faq-answer" />
            </div>
            <div className="space-y-2">
              <Label>Category (optional grouping)</Label>
              <Input value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} placeholder="e.g., Shipping, Storage, Side effects" data-testid="faq-category" />
            </div>
            <div className="flex items-center gap-3">
              <Switch checked={form.is_active} onCheckedChange={(v) => setForm({ ...form, is_active: v })} data-testid="faq-active" />
              <Label>Visible on site</Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancel</Button>
            <Button onClick={save} data-testid="save-faq" className="bg-[#C2410C] hover:bg-[#9A3412]">Save FAQ</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
